import React, { useRef, useState } from "react";
import { Image as ImageIcon, Link as LinkIcon, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Reusable thumbnail picker. Supports device upload (→ /uploads/image) AND paste a URL.
 * Calls onChange(url) whenever the current value changes.
 *
 * Props:
 *   value: current thumbnail URL string
 *   onChange: (url) => void
 *   testId: base data-testid (defaults to "thumbnail-picker")
 *   label: caption above the control
 */
export default function ThumbnailPicker({ value, onChange, testId = "thumbnail-picker", label = "Thumbnail" }) {
  const [mode, setMode] = useState(value ? "url" : "upload");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `/api/files/${data.storage_path}`;
      onChange(url);
      toast.success("Thumbnail uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid={testId} className="space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</div>
      <div className="flex gap-1 p-1 bg-white/5 border border-white/10 rounded-full w-fit">
        <button
          type="button"
          onClick={() => setMode("upload")}
          data-testid={`${testId}-tab-upload`}
          className={`px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1 transition ${mode === "upload" ? "bg-tv-accent text-black" : "text-white/70 hover:text-white"}`}
        >
          <Upload className="w-3 h-3" /> Upload
        </button>
        <button
          type="button"
          onClick={() => setMode("url")}
          data-testid={`${testId}-tab-url`}
          className={`px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1 transition ${mode === "url" ? "bg-tv-accent text-black" : "text-white/70 hover:text-white"}`}
        >
          <LinkIcon className="w-3 h-3" /> Paste link
        </button>
      </div>

      {mode === "upload" ? (
        <label className={`flex items-center gap-3 px-4 py-3 rounded-lg border border-dashed border-white/20 bg-white/[0.02] cursor-pointer hover:bg-white/[0.04] ${busy ? "opacity-60" : ""}`}>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} data-testid={`${testId}-file-input`} />
          <ImageIcon className="w-5 h-5 text-tv-accent" />
          <div className="flex-1">
            <div className="text-sm text-white font-semibold">{busy ? "Uploading…" : "Choose image from device"}</div>
            <div className="text-[11px] text-white/40">PNG / JPG / WebP · recommended 16:9</div>
          </div>
        </label>
      ) : (
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…image.jpg"
          data-testid={`${testId}-url-input`}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
        />
      )}

      {value && (
        <div className="relative mt-2 rounded-lg overflow-hidden bg-black" data-testid={`${testId}-preview`}>
          <img src={value} alt="" className="w-full max-h-56 object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testId}-clear`}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-red-500/80"
            aria-label="Clear thumbnail"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
